/**
 * BB Special Order — global pill click handler + page personalization.
 *
 * Feature 1: Click the SPECIAL ORDER pill in the BB Shipping Estimator →
 *            smooth-scroll to the nearest Special Order callout (cart line item,
 *            PDP mesh-swap callout, or /pages/special-order body) and flash it.
 *
 * Feature 2: On /pages/special-order — read /cart.js, if there are SO items,
 *            inject a personalized "Your Special Order" summary card at the top
 *            of the page showing each line, the customer's zone, and ETA.
 */
(function () {
  'use strict';

  function isSoMesh(val) { return false; } // 2026-06-10 SO RETIRED — docs/special-order-retirement-2026-06-10.md
  function _isSoMeshRetired(val) {
    if (!val) return false;
    return /\b1\s*["'′″“”’]?\s*[x×]\s*1\b/i.test(val)
        || /\b2\s*["'′″“”’]?\s*[x×]\s*6\b/i.test(val);
  }

  // ─── FEATURE 1 — Pill click highlights the SO callout ─────────────
  function flashHighlight(el) {
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    var origShadow = el.style.boxShadow || '';
    var origTransition = el.style.transition || '';
    el.style.transition = 'box-shadow 0.4s ease';
    el.style.boxShadow = '0 0 0 4px rgba(240,173,78,0.6)';
    setTimeout(function () {
      el.style.boxShadow = '0 0 0 0 rgba(240,173,78,0)';
      setTimeout(function () {
        el.style.boxShadow = origShadow;
        el.style.transition = origTransition;
      }, 400);
    }, 1400);
  }

  function findSoTarget() {
    // Priority: in-cart-context line item, then PDP mesh-swap, then personalized summary, then page
    return document.querySelector('.CartItem__SpecialOrder')
        || document.getElementById('bb-mesh-swap')
        || document.getElementById('bb-so-personalized')
        || document.querySelector('.so-hero, .so-section');
  }

  function bindPillClicks() {
    document.addEventListener('click', function (e) {
      var pill = e.target.closest('.bb-ship-so-pill');
      if (!pill) return;
      var target = findSoTarget();
      if (target) {
        e.preventDefault();
        flashHighlight(target);
      } else if (window.location.pathname.indexOf('/pages/special-order') === -1) {
        // No SO element on the page → bounce to the explainer page
        e.preventDefault();
        window.location.href = '/pages/special-order';
      }
    });
    // Make the pill obviously clickable + center SO callout text on mobile
    if (!document.getElementById('bb-so-pill-style')) {
      var style = document.createElement('style');
      style.id = 'bb-so-pill-style';
      style.textContent =
        '.bb-ship-so-pill{cursor:pointer;transition:background 0.15s;}' +
        '.bb-ship-so-pill:hover{background:#f0ad4e!important;color:#fff!important;}' +
        '@media (max-width:600px){' +
          '.CartItem__SpecialOrder,#bb-mesh-swap,#bb-so-personalized{text-align:center;}' +
          '.CartItem__SpecialOrder a,#bb-mesh-swap a,#bb-so-personalized a{margin-left:auto!important;margin-right:auto!important;}' +
          '.CartItem__SO_AltBtn{display:block!important;margin:6px auto 0!important;}' +
        '}';
      document.head.appendChild(style);
    }
  }

  // ─── FEATURE 2 — Personalize /pages/special-order with cart state ─
  async function fetchCart() {
    try {
      var r = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      return r.json();
    } catch (e) { return null; }
  }

  function getCustomerZoneLabel() {
    // Estimator caches it in sessionStorage
    var state = sessionStorage.getItem('bb_geo_state') || '';
    var city = sessionStorage.getItem('bb_geo_city') || '';
    if (city && state) return city + ', ' + state;
    return state || null;
  }

  // Per-product differentiators — surfaced on the SO page so customers see exactly
  // what makes their order worth the wait, vs competing alternatives.
  var DIFFERENTIATORS = {
    'hog-wire-fence-panels': [
      '6-gauge welded steel — most competitors use 9–12 gauge wire that bends in storms',
      'Dip-coated finish for full corrosion coverage — competitors powder-coat which chips',
      'Pre-framed in Western Red Cedar or pressure-treated lumber — competitors sell loose mesh',
      '25-year structural warranty (rated past 40 years)'
    ],
    'hog-wire-deck-railing': [
      'Code-compliant 36"/42" deck heights with welded steel — most competitors knock off chain-link',
      'Pre-assembled panels ready to install — saves 8–12 hours vs framing from scratch',
      'Marine-grade fasteners included — competitors charge $40–80 extra'
    ],
    'cattle-panel-fence': [
      '6-gauge welded grid — heavier than competing "cattle panel" SKUs (most are 10-gauge)',
      'Coordinated colors across panels, gates, and corners — competitors mix-and-match suppliers',
      'Available framed or unframed — most competitors offer one or the other only'
    ],
    'aluminum': [
      'Welded 6063-T5 alloy panels — competitors use snap-together construction that loosens',
      'Heavy-wall 14-gauge frame — competing aluminum is 18–20 gauge hollow',
      'Powder-coated for 8+ year color hold — competing finishes fade in 2–3 years'
    ],
    'brown-frame-metal': [
      'Zinc-Magnesium (ZM) coating outlasts galvanized 3× — competing kits use plain galvanized',
      'KDBT or Western Red Cedar wood-frame option — competing metal fences are pure metal/vinyl',
      'Pre-cut and pre-drilled — competing kits require on-site fabrication',
      '25-year structural warranty'
    ],
    'steel-board': [
      'Solid steel boards — competing "steel-look" fencing is vinyl wrap over plastic',
      'Stainless fasteners included — competitors charge extra or use galvanized that streaks',
      'Heights up to 8 ft — competitors cap at 6 ft',
      'Coordinates with our hog-wire and cattle-panel lines'
    ],
    '316-stainless': [
      '316 marine-grade stainless (vs cheaper 304) — won’t rust in oceanfront / poolside installs',
      'Welded panels — competitors weave 316 wire which loosens over time',
      'Sourced and welded in North America — most competing 316 SS is imported',
      'Lifetime corrosion warranty'
    ],
    'green-hog-wire': [
      'True dark-green dip-coat — blends into vegetation (most competitors offer black or galvanized only)',
      'Same 6-gauge welded steel as our flagship hog-wire line',
      'Batch-produced in small runs — one of the only North American makers still producing it',
      '25-year structural warranty'
    ],
    'rust-patina': [
      'Naturally weathered Corten patina — the rust IS the protective layer (no maintenance needed)',
      'Architectural-grade visual character — different look every install',
      'Welded construction — competing "rusted" wire is sprayed paint that flakes off',
      'Discontinued at most retailers — we still produce in small runs'
    ]
  };

  function getDifferentiators(handle) {
    var h = String(handle || '').toLowerCase();
    if (/316-stainless/.test(h)) return DIFFERENTIATORS['316-stainless'];
    if (/aluminum/.test(h)) return DIFFERENTIATORS['aluminum'];
    if (/steel-board/.test(h)) return DIFFERENTIATORS['steel-board'];
    if (/brown-frame-metal/.test(h)) return DIFFERENTIATORS['brown-frame-metal'];
    if (/rust-patina/.test(h) || /rust-cattle/.test(h)) return DIFFERENTIATORS['rust-patina'];
    if (/green/.test(h) && /hog/.test(h)) return DIFFERENTIATORS['green-hog-wire'];
    if (/green/.test(h) && /cattle/.test(h)) return DIFFERENTIATORS['cattle-panel-fence'];
    if (/deck-railing/.test(h)) return DIFFERENTIATORS['hog-wire-deck-railing'];
    if (/cattle-panel/.test(h)) return DIFFERENTIATORS['cattle-panel-fence'];
    if (/hog-wire/.test(h)) return DIFFERENTIATORS['hog-wire-fence-panels'];
    return null;
  }

  // Compute SO-adjusted delivery range (production + zone transit) directly,
  // since the estimator widget's date may not reflect SO at page-load time.
  function computeSODateRange() {
    var state = sessionStorage.getItem('bb_geo_state');
    if (!state) return null;
    var US_ZONES = {'WA':1,'OR':1,'CA':1,'ID':2,'NV':2,'AZ':2,'NM':2,'UT':2,'WY':2,'CO':2,'MT':2,'AL':3,'CT':3,'DE':3,'FL':3,'GA':3,'IN':3,'KY':3,'ME':3,'MD':3,'MA':3,'MI':3,'MS':3,'NH':3,'NJ':3,'NY':3,'NC':3,'OH':3,'PA':3,'RI':3,'SC':3,'TN':3,'VT':3,'VA':3,'WV':3,'DC':3,'NE':4,'ND':4,'SD':4,'IL':4,'MO':4,'IA':4,'LA':4,'MN':4,'WI':4,'KS':4,'OK':4,'TX':5};
    var CA_ZONES = {'BC':1,'AB':2,'SK':3,'MB':4,'NB':4,'NS':4,'ON':4,'QC':4,'PE':4,'NL':4};
    var US_TRANSIT = {1:[1,10],2:[2,12],3:[5,18],4:[3,14],5:[3,12]};
    var CA_TRANSIT = {1:[1,7],2:[1,14],3:[14,21],4:[21,35]};
    var transit = null;
    if (US_ZONES[state]) transit = US_TRANSIT[US_ZONES[state]];
    else if (CA_ZONES[state]) transit = CA_TRANSIT[CA_ZONES[state]];
    if (!transit) return null;
    var PROD_MIN = 56, PROD_MAX = 56; // match bb-shipping-estimator's fixed 8-week production
    var earliest = new Date(Date.now() + (PROD_MIN + transit[0]) * 86400000);
    var latest = new Date(Date.now() + (PROD_MAX + transit[1]) * 86400000);
    var M = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return M[earliest.getMonth()] + ' ' + earliest.getDate() + ' – ' + M[latest.getMonth()] + ' ' + latest.getDate();
  }

  function moneyFmt(cents) {
    return '$' + (Math.round((cents || 0) / 100)).toLocaleString('en-US');
  }

  function escapeHtml(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function variantReason(item) {
    // Why is THIS line Special Order? (variant mesh, or product-level tag)
    var opts = item.options_with_values || [];
    for (var k = 0; k < opts.length; k++) {
      var n = String(opts[k].name || '').toLowerCase();
      if (/wire.?opening|mesh/.test(n) && isSoMesh(opts[k].value)) {
        return '<strong>' + escapeHtml(opts[k].value) + '</strong> mesh — custom batch run';
      }
    }
    var tags = item.product_tags || [];
    for (var j = 0; j < tags.length; j++) {
      if (String(tags[j]).toLowerCase() === 'special-order') {
        var t = (item.product_type || '').toLowerCase();
        if (/aluminum|aluminium/.test(t)) return 'Aluminum finish — custom coating batch';
        if (/metal\s*fenc/.test(t) && /zero.?maintenance/i.test(item.product_title || '')) return 'ZeroMaintenance kit — custom batch run';
        if (/hog wire/i.test(t)) return 'Custom colour finish — patina batch run';
        return 'Custom finish — batch run';
      }
    }
    return 'Custom production batch';
  }

  function personalizeSpecialOrderPage(cart) {
    if (!cart || !cart.items || !cart.items.length) return;
    // Whole-product SO handles — /cart.js does NOT return product_tags reliably,
    // so we also match by handle for these tagged products.
    var SO_PRODUCT_HANDLES = {
      'rust-patina-hog-wire-fence-panels-hog-panels-premade-or-unframed-the-red-rock':1,
      'rust-patina-hog-wire-deck-railing-panels-premade-or-unframed-the-copperhead':1,
      'rust-cattle-panel-fence-cattle-panels-premade-or-unframed-the-aberdeen':1,
      'black-aluminum-privacy-fence-panels-96-wide-x-96-high':1,
      'black-aluminum-privacy-fence-panels-69-high':1,
      'black-aluminum-privacy-fence-panels-46-high':1,
      'black-aluminum-screen-fence-panels-46-high':1,
      'black-aluminum-screen-fence-panels-72-wide-x-69-high':1,
      'black-aluminum-privacy-gate-40-wide-x-44-5-high':1,
      'black-aluminum-privacy-gate-40-wide-x-67-5-high':1,
      'black-aluminum-screen-gates-40-wide-x-44-5-high':1,
      'brown-frame-metal-privacy-fence-kit':1,
      'dark-green-hog-wire-deck-railing-panels-premade-or-unframed-the-cypress':1,
      '316-stainless-steel-wire-fence-panels':1,
      '316-stainless-steel-deck-railings':1,
      '316-stainless-steel-wire-deck-railing':1,
      'green-hog-wire-fence-panels-hog-panels-premade-or-unframed-the-amazon':1,
      'green-cattle-panel-fence-cattle-panels-premade-or-unframed-the-galloway':1
    };
    var soItems = cart.items.filter(function (it) {
      if (it.handle && SO_PRODUCT_HANDLES[it.handle]) return true;
      var tags = it.product_tags || [];
      for (var j = 0; j < tags.length; j++) {
        if (String(tags[j]).toLowerCase() === 'special-order') return true;
      }
      var opts = it.options_with_values || [];
      for (var k = 0; k < opts.length; k++) {
        var n = String(opts[k].name || '').toLowerCase();
        if (/wire.?opening|mesh/.test(n) && isSoMesh(opts[k].value)) return true;
      }
      return false;
    });
    if (!soItems.length) return;

    var zone = getCustomerZoneLabel();
    var subtotalSO = soItems.reduce(function (a, it) { return a + (it.line_price || it.price * it.quantity || 0); }, 0);

    // Compute SO-adjusted ETA directly (production + zone transit). Falls back to whatever
    // the estimator rendered if we can't compute (no geo state).
    var etaRange = computeSODateRange();
    if (!etaRange) {
      var rangeEl = document.querySelector('#bb-ship-header-cart strong, #bb-ship-header-product strong');
      etaRange = rangeEl ? rangeEl.textContent.trim() : null;
    }

    var rows = soItems.map(function (it) {
      var variantTitle = it.variant_title || '';
      var imgUrl = it.image || it.featured_image?.url || '';
      var img = imgUrl ? '<img src="' + escapeHtml(imgUrl) + '" alt="" style="width:64px;height:64px;object-fit:cover;border-radius:6px;flex:0 0 64px;">' : '';
      var diffs = getDifferentiators(it.handle);
      var diffsHtml = '';
      if (diffs && diffs.length) {
        diffsHtml = '<div style="margin-top:8px;padding:10px 12px;background:#fff;border-left:3px solid #EB673C;border-radius:4px;">' +
          '<div style="font-size:11px;font-weight:700;color:#7a4a00;text-transform:uppercase;letter-spacing:0.4px;margin-bottom:6px;">Why this is worth the wait</div>' +
          '<ul style="margin:0;padding:0 0 0 18px;font-size:12.5px;color:#3a2800;line-height:1.5;">' +
            diffs.slice(0, 4).map(function (d) { return '<li style="margin:2px 0;">' + escapeHtml(d) + '</li>'; }).join('') +
          '</ul>' +
        '</div>';
      }
      return '<div style="display:flex;gap:12px;align-items:flex-start;padding:12px 0;border-bottom:1px solid #f0e0c0;flex-wrap:wrap;">' +
        '<div style="display:flex;gap:12px;align-items:flex-start;flex:1;min-width:280px;">' +
          img +
          '<div style="flex:1;min-width:0;">' +
            '<div style="font-weight:600;color:#1a1a1a;font-size:14px;">' + escapeHtml(it.product_title || it.title) + '</div>' +
            (variantTitle ? '<div style="font-size:12px;color:#666;margin-top:2px;">' + escapeHtml(variantTitle) + '</div>' : '') +
            '<div style="font-size:12px;color:#7a4a00;margin-top:4px;">Why Special Order: ' + variantReason(it) + '</div>' +
          '</div>' +
        '</div>' +
        '<div style="font-size:13px;font-weight:600;text-align:right;color:#1a1a1a;flex:0 0 auto;">' + it.quantity + ' × ' + moneyFmt(it.price) + '</div>' +
        '<div style="flex:1 0 100%;">' + diffsHtml + '</div>' +
      '</div>';
    }).join('');

    var card = document.createElement('div');
    card.id = 'bb-so-personalized';
    card.style.cssText = 'background:linear-gradient(135deg,#fff8e1 0%,#fff3cd 100%);border:2px solid #f0ad4e;border-radius:12px;padding:24px;margin:0 0 32px;color:#1a1a1a;';
    card.innerHTML =
      '<div style="display:flex;align-items:center;gap:10px;margin-bottom:6px;">' +
        '<span style="display:inline-block;background:#EB673C;color:#fff;padding:3px 10px;border-radius:20px;font-size:11px;font-weight:700;letter-spacing:0.8px;text-transform:uppercase;">Your Order</span>' +
        '<span style="font-size:12px;color:#7a4a00;">' + soItems.length + ' Special Order item' + (soItems.length === 1 ? '' : 's') + ' in your cart</span>' +
      '</div>' +
      '<h2 style="margin:0 0 4px;font-size:24px;color:#1a1a1a;border:none;padding:0;">Your Special Order at a glance</h2>' +
      '<p style="margin:0 0 16px;font-size:14px;color:#5a3700;">Everything below is tailored to what&rsquo;s currently in your cart. The timeline, manufacturing steps, and warranty all apply to <em>your</em> items.</p>' +
      '<div>' + rows + '</div>' +
      '<div style="display:flex;gap:16px;margin-top:16px;flex-wrap:wrap;">' +
        '<div style="flex:1;min-width:160px;background:#fff;border-radius:8px;padding:12px 16px;border:1px solid #f0ad4e;">' +
          '<div style="font-size:11px;color:#7a4a00;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Subtotal (Special Order)</div>' +
          '<div style="font-size:22px;font-weight:800;color:#1a1a1a;margin-top:2px;">' + moneyFmt(subtotalSO) + '</div>' +
        '</div>' +
        '<div style="flex:1;min-width:160px;background:#fff;border-radius:8px;padding:12px 16px;border:1px solid #f0ad4e;">' +
          '<div style="font-size:11px;color:#7a4a00;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Estimated delivery' + (zone ? ' to ' + escapeHtml(zone) : '') + '</div>' +
          '<div style="font-size:18px;font-weight:700;color:#1a1a1a;margin-top:2px;">' + (etaRange ? escapeHtml(etaRange) : '6&ndash;8 weeks production + standard zone delivery') + '</div>' +
        '</div>' +
        '<div style="flex:1;min-width:160px;background:#fff;border-radius:8px;padding:12px 16px;border:1px solid #f0ad4e;">' +
          '<div style="font-size:11px;color:#7a4a00;text-transform:uppercase;letter-spacing:0.5px;font-weight:600;">Production window</div>' +
          '<div style="font-size:18px;font-weight:700;color:#1a1a1a;margin-top:2px;">6&ndash;8 weeks</div>' +
        '</div>' +
      '</div>' +
      '<div style="margin-top:16px;display:flex;gap:8px;flex-wrap:wrap;">' +
        '<a href="/cart" style="display:inline-block;background:#1a1a1a;color:#fff;padding:10px 20px;border-radius:6px;text-decoration:none;font-weight:600;font-size:13px;">Review my cart &raquo;</a>' +
        '<a href="/checkout" style="display:inline-block;background:#EB673C;color:#fff;padding:10px 20px;border-radius:6px;text-decoration:none;font-weight:600;font-size:13px;">Place my Special Order &raquo;</a>' +
      '</div>';

    var anchor = document.querySelector('.so-hero')
              || document.querySelector('.shopify-section')
              || document.querySelector('main')
              || document.body.firstElementChild;
    if (anchor && anchor.parentNode) {
      anchor.parentNode.insertBefore(card, anchor.nextSibling);
    }
  }

  async function init() {
    bindPillClicks();
    if (window.location.pathname.indexOf('/pages/special-order') !== -1) {
      var cart = await fetchCart();
      personalizeSpecialOrderPage(cart);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
